
import React, { useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, Edit2, Check, X } from "lucide-react";
import DraggableSubtask from './DraggableSubtask';
import { ProjectSubtask, StepColumn as StepColumnType } from '../types';

interface StepColumnProps {
  stepColumn: StepColumnType;
  onEdit: (subtask: ProjectSubtask) => void;
  onDelete: (subtaskId: string) => void;
  onStatusChange: (subtaskId: string, status: 'To Do' | 'In Progress' | 'Done') => void;
  onDeleteStep: (step: number) => void;
  onEditStep: (step: number, newName: string) => void;
  canDeleteStep: boolean;
  isRestrictedUser?: boolean;
}

const StepColumn = ({ 
  stepColumn, 
  onEdit, 
  onDelete, 
  onStatusChange, 
  onDeleteStep, 
  onEditStep,
  canDeleteStep,
  isRestrictedUser = false
}: StepColumnProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editingName, setEditingName] = useState(stepColumn.step_name);
  
  const { setNodeRef } = useDroppable({
    id: `step-${stepColumn.step}`,
  });

  // Sort subtasks alphabetically within the step
  const sortedSubtasks = [...stepColumn.subtasks].sort((a, b) => 
    a.subtask_name.localeCompare(b.subtask_name)
  );

  const handleEditStart = () => {
    setIsEditing(true);
    setEditingName(stepColumn.step_name);
  };

  const handleEditSave = () => {
    if (editingName.trim() && editingName !== stepColumn.step_name) {
      onEditStep(stepColumn.step, editingName.trim());
    }
    setIsEditing(false);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setEditingName(stepColumn.step_name);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleEditSave();
    } else if (e.key === 'Escape') {
      handleEditCancel();
    }
  };

  // Updated to use the delete handler from TaskManagementTab which shows confirmation dialog
  const handleDeleteStep = () => {
    onDeleteStep(stepColumn.step);
  };

  return (
    <div className="flex-shrink-0 w-72">
      <Card className="bg-secondary/20 border border-white h-full w-[280px] flex flex-col">
        <CardHeader className="pb-2 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              {isEditing ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={handleKeyPress}
                    className="text-black bg-white border-gray-300 text-sm h-6 px-2"
                    autoFocus
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleEditSave}
                    className="text-green-300 hover:text-green-200 hover:bg-green-500/20 h-6 w-6 p-0"
                  >
                    <Check className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleEditCancel}
                    className="text-red-300 hover:text-red-200 hover:bg-red-500/20 h-6 w-6 p-0"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <div>
                  <h3 className="text-white font-medium text-sm">{stepColumn.step_name}</h3>
                  <p className="text-gray-400 text-xs">
                    {stepColumn.subtasks.length} subtask{stepColumn.subtasks.length !== 1 ? 's' : ''}
                  </p>
                </div>
              )}
            </div>
            
            {!isEditing && (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleEditStart}
                  disabled={isRestrictedUser}
                  className={`transition-colors h-6 w-6 p-0 ${
                    isRestrictedUser 
                      ? "text-gray-500 cursor-not-allowed hover:text-gray-500 hover:bg-transparent"
                      : "text-blue-300 hover:text-blue-200 hover:bg-blue-500/20"
                  }`}
                >
                  <Edit2 className="h-3 w-3" />
                </Button>
                {canDeleteStep && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDeleteStep}
                    disabled={isRestrictedUser}
                    className={`transition-colors h-6 w-6 p-0 ${
                      isRestrictedUser 
                        ? "text-gray-500 cursor-not-allowed hover:text-gray-500 hover:bg-transparent"
                        : "text-red-300 hover:text-red-200 hover:bg-red-500/20"
                    }`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-0 flex-1 overflow-hidden min-h-0">
          <div 
            ref={setNodeRef}
            className="h-full overflow-y-auto space-y-2 p-2 bg-secondary/10 rounded border-2 border-dashed border-primary/20"
          >
            <SortableContext items={sortedSubtasks.map(s => s.id)} strategy={verticalListSortingStrategy}>
              {sortedSubtasks.map((subtask) => (
                <DraggableSubtask
                  key={subtask.id}
                  subtask={subtask}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onStatusChange={onStatusChange}
                  isRestrictedUser={isRestrictedUser}
                />
              ))}
            </SortableContext>
            {stepColumn.subtasks.length === 0 && (
              <div className="text-center text-gray-400 text-sm py-8">
                Drop subtasks here
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StepColumn;
